import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import { Navigate } from "react-router-dom";
import { Users, CalendarClock, Search, X } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PageNotFound from "@/lib/PageNotFound";

import ClientProfileDialog from "@/components/trainer/ClientProfileDialog";
import ClientSessionsDialog from "@/components/trainer/ClientSessionsDialog";
import { getMembershipConfig, normaliseTier, formatMembership } from "@/lib/membershipUtils";

export default function TrainerClients() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: trainers = [] } = useQuery({
    queryKey: ["trainers"],
    queryFn: () => base44.entities.Trainer.list(),
  });

  const isTrainer =
    user?.role === "admin" ||
    user?.role === "trainer" ||
    trainers.some(t => t.user_id === user?.id && t.is_active);

  const [profileClient, setProfileClient] = useState(null);
  const [sessionsClient, setSessionsClient] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [membershipFilter, setMembershipFilter] = useState("all");

  const { data: members = [], isLoading: loadingMembers } = useQuery({
    queryKey: ["trainerClients"],
    queryFn: () => base44.entities.User.list(),
  });

  const { data: workouts = [], isLoading: loadingWorkouts } = useQuery({
    queryKey: ["allWorkouts"],
    queryFn: () => base44.entities.WorkoutPlan.list(),
  });

  const { data: templates = [] } = useQuery({
    queryKey: ["workoutTemplates"],
    queryFn: () => base44.entities.WorkoutTemplate.list(),
  });

  const assignMutation = useMutation({
    mutationFn: ({ client, template }) =>
      base44.entities.WorkoutPlan.create({
        name: template.name,
        description: template.description,
        difficulty: template.difficulty,
        goal: template.goal,
        days_per_week: template.days_per_week,
        exercises: template.exercises,
        is_active: true,
        assigned_to_user_id: client.id,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["allWorkouts"] }),
  });

  if (!isTrainer) return <PageNotFound />;

  const activeClients = members.filter(
    (m) =>
      normaliseTier(m.membership_type) !== "foundation" &&
      m.role !== "trainer" &&
      m.role !== "admin"
  );

  const filteredClients = activeClients.filter(client => {
    const matchesSearch = searchQuery === "" ||
      client.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.email?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesMembership = membershipFilter === "all" || normaliseTier(client.membership_type) === membershipFilter;

    return matchesSearch && matchesMembership;
  });

  const getClientWorkouts = (clientId) =>
    workouts.filter(
      (w) => (w.created_by_id === clientId || w.assigned_to_user_id === clientId) && w.is_active
    );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-display font-bold tracking-tight">My Clients</h1>
        <p className="text-muted-foreground mt-1">Active clients and their latest workout plans</p>
      </div>

      {loadingMembers || loadingWorkouts ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
        </div>
      ) : activeClients.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-16 text-center">
            <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">No active clients yet</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex gap-3 items-end">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-muted rounded transition-colors"
                >
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              )}
            </div>
            <Select value={membershipFilter} onValueChange={setMembershipFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Filter by membership" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Memberships</SelectItem>
                <SelectItem value="momentum">Momentum</SelectItem>
                <SelectItem value="strength">Strength</SelectItem>
                <SelectItem value="elite_coaching">Elite Coaching</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {filteredClients.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-16 text-center">
                <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {searchQuery || membershipFilter !== "all" ? "No clients match your filters" : "No active clients yet"}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredClients.map((client) => {
            const clientWorkouts = getClientWorkouts(client.id);
            return (
              <Card key={client.id} className="border-0 shadow-sm overflow-hidden flex flex-col cursor-pointer hover:shadow-md transition-shadow" onClick={() => setProfileClient(client)}>
                <CardContent className="pt-5 pb-4 flex flex-col items-center text-center gap-2">
                  {client.picture_url ? (
                    <img src={client.picture_url} alt={client.full_name} className="w-16 h-16 rounded-full object-cover shrink-0" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-2xl shrink-0">
                      {(client.full_name || client.email || "?")[0].toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 w-full">
                    <p className="font-semibold truncate">{client.full_name || "—"}</p>
                    <p className="text-xs text-muted-foreground truncate">{client.email}</p>
                  </div>
                  <Badge variant="secondary" className={getMembershipConfig(client.membership_type).className}>
                    {formatMembership(client.membership_type)}
                  </Badge>
                  <Button
                    size="sm" variant="outline"
                    className="gap-1 text-xs w-full mt-1"
                    onClick={(e) => { e.stopPropagation(); setSessionsClient(client); }}
                  >
                    <CalendarClock className="w-3 h-3" /> Sessions
                  </Button>
                </CardContent>
              </Card>
              );
              })}
            </div>
          )}
        </>
      )}

      <ClientProfileDialog
        client={profileClient}
        open={!!profileClient}
        onOpenChange={(o) => { if (!o) setProfileClient(null); }}
        workouts={profileClient ? getClientWorkouts(profileClient.id) : []}
        templates={templates}
      />
      <ClientSessionsDialog
        client={sessionsClient}
        open={!!sessionsClient}
        onOpenChange={(o) => { if (!o) setSessionsClient(null); }}
      />

    </div>
  );
}